import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { routeTransitionAnimation } from '../../shared/animations/page.animations';
import { environment } from '../../../environments/environment';
import { ShellNavService } from './shell-nav.service';
import { AuthService } from '../../core/services/auth.service';
import { AuthConnectionError } from '../../shared/components/auth-connection-error/auth-connection-error';

interface NavItem {
  label: string;
  path: string;
  iconOutline: string;
  iconSolid: string;
  isCenter?: boolean;
  matchPaths?: string[];
}

/** Routes that should render without the bottom tab bar (detail / success flows). */
const HIDE_NAV_PREFIXES = [
  '/mission/details',
  '/rewards/reward-details',
  '/activity',
  '/notifications/details',
  '/my-rewards',
  '/rewards/redeem-confirm',
  '/rewards/redeem-success',
  '/tier-benefits',
  '/vip-tiers',
  '/points-history',
  '/coins-history',
  '/faqs',
  '/privacy',
  '/terms',
  '/news',
  '/profile',
];

/**
 * Public shell routes reachable from `/app` deeplinks without an existing Tinbo session
 * (mobile Intent fallback + desktop web).
 */
const PUBLIC_SHELL_PREFIXES = [
  '/news/',
  '/mission/details',
  '/rewards/reward-details',
  '/privacy',
  '/terms',
];

function normalizeShellUrl(url: string): string {
  const path = url.split('?')[0]?.split('#')[0] ?? '';
  if (!path) {
    return '/';
  }
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  return withSlash.length > 1 && withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash;
}

function matchesUrlPrefix(url: string, prefix: string): boolean {
  return url === prefix || url.startsWith(`${prefix}/`);
}

function shouldHideNavForUrl(url: string): boolean {
  const normalized = normalizeShellUrl(url);
  return HIDE_NAV_PREFIXES.some((prefix) => matchesUrlPrefix(normalized, prefix));
}

function isPublicShellUrl(url: string): boolean {
  const normalized = normalizeShellUrl(url);
  return PUBLIC_SHELL_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) {
      return normalized.startsWith(prefix);
    }
    return matchesUrlPrefix(normalized, prefix);
  });
}

@Component({
  selector: 'app-shell',
  imports: [RouterOutlet, RouterLink, AuthConnectionError],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
  animations: [routeTransitionAnimation],
})
export class Shell {
  private readonly router = inject(Router);
  private readonly shellNav = inject(ShellNavService);
  private readonly authService = inject(AuthService);

  protected readonly isAuthenticated = this.authService.isAuthenticated;

  /** Deeplink article destinations must render even before Tinbo auth is established. */
  protected readonly allowUnauthenticatedContent = computed(() =>
    isPublicShellUrl(this.routeKey()),
  );

  protected readonly navItems: NavItem[] = [
    {
      label: 'Home',
      path: '/home',
      iconOutline: 'icon--home-outline',
      iconSolid: 'icon--home-solid',
    },
    {
      label: 'Missions',
      path: '/mission/list',
      iconOutline: 'icon--target-arrow-outline',
      iconSolid: 'icon--target-arrow-solid',
      matchPaths: ['/missions', '/mission'],
    },
    {
      label: 'My Rewards',
      path: '/rewards',
      iconOutline: 'icon--star-outline',
      iconSolid: 'icon--star-solid',
      isCenter: true,
      matchPaths: ['/rewards', '/my-rewards'],
    },
    {
      label: 'Notifications',
      path: '/notifications',
      iconOutline: 'icon--bell-outline',
      iconSolid: 'icon--bell-solid',
    },
    {
      label: 'Account',
      path: '/account',
      iconOutline: 'icon--user-1-outline',
      iconSolid: 'icon--user-1-solid',
      matchPaths: ['/account', '/privacy', '/terms', '/faqs', '/profile'],
    },
  ];

  protected readonly routeKey = signal(normalizeShellUrl(this.router.url));

  protected readonly hideNav = computed(
    () => shouldHideNavForUrl(this.routeKey()) || this.shellNav.isHidden(),
  );

  protected readonly showNav = computed(() => this.isAuthenticated());

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = normalizeShellUrl((event as NavigationEnd).urlAfterRedirects);
        this.routeKey.set(url);
        if (!shouldHideNavForUrl(url)) {
          this.shellNav.show();
        }
      });
  }

  protected isNavActive(item: NavItem): boolean {
    const url = normalizeShellUrl(this.routeKey());

    if (item.matchPaths?.length) {
      return item.matchPaths.some((path) => matchesUrlPrefix(url, path));
    }

    return matchesUrlPrefix(url, item.path);
  }

  protected navIconClass(item: NavItem): string {
    const glyph = this.isNavActive(item) ? item.iconSolid : item.iconOutline;
    return `icon icon--24 ${glyph}`;
  }

  protected backToTinbo(): void {
    window.location.href = environment.tinboAppLinkBase;
  }
}
